"use client"

import React from 'react'; // Import React
import { createContext, useContext, useState, type ReactNode, useEffect, useCallback, useRef } from "react"
import { v4 as uuidv4 } from 'uuid'; // Need to install uuid: npm install uuid @types/uuid

import { MAX_ANALYSIS_WORDS } from "@/lib/analysis-limits"

// Import new discriminated union types
import type {
  AnalysisTarget,
  OperantAnalysisTarget,
  MixedRespondentOperantTarget,
  RespondentAnalysisTarget,
  NonAssociativeAnalysisTarget,
  LegacyBehavior,
} from "@/types/analysis-targets"
import { migrateToAnalysisTarget } from "@/types/analysis-targets"

// --- Add null safety utilities ---
const safeString = (str: string | undefined | null): string => str || "";
const safeArray = <T,>(arr: T[] | undefined | null): T[] => arr || [];
const safeWordCount = (text: string | undefined | null): number => {
  if (!text || typeof text !== 'string') return 0;
  return text.split(/\s+/).filter(Boolean).length;
};

// --- Operant-first utilities ---
// Normalize behavior type according to operant-first approach
const normalizeBehaviorType = (type: BehaviorType | undefined | null): BehaviorType => {
  // If type is undefined, null, or "unknown", default to "operant"
  if (!type || type === "unknown") {
    return "operant";
  }
  return type;
};

// --- Add FunctionalAnalysisItem Type ---
export interface FunctionalAnalysisItem {
  type: string;
  description: string;
  evidence_quote?: string | null;
}

// --- Add Composable Antecedent Types ---
export interface OperantAntecedentItem {
  item_type: "operant_antecedent";
  type: "Discriminative Stimulus (SD)";
  description: string;
  evidence_quote?: string | null;
}

export interface RespondentChainItem {
  item_type: "respondent_chain";
  description: string;
  evidence_quote?: string | null;
  // Nested respondent analysis components
  us: FunctionalAnalysisItem[];
  ur: FunctionalAnalysisItem[];
  cs: FunctionalAnalysisItem[];
  cr: FunctionalAnalysisItem[];
}

// Union type for antecedents - can be either operant antecedent or respondent chain
export type AnyAntecedent = OperantAntecedentItem | RespondentChainItem;
// --- End Add --- 

// --- Add BehaviorType Type ---
export type BehaviorType = "operant" | "respondent" | "respondent_chain" | "non_associative" | "unconditioned" | "unknown"
// --- End Add --- 

// --- Add CaseNote Type ---
export interface CaseNote {
  id: string;
  title: string;
  content: string;
  source: 'manual' | 'upload' | 'paste' | 'audio';
  createdAt: number;
  lastModified: number;
  wordCount?: number;
  filename?: string; // For upload-sourced notes
}
// --- End Add ---

// Define types for our context
export interface Frequency {
  value: number | null
  unit: string
}

export interface Duration {
  value: number | null
  unit:string
}

export interface Behavior {
  id: string
  name: string
  description: string
  type: BehaviorType;
  frequency: Frequency;
  duration: Duration;
  intensity: number
  // Composable antecedents - can be operant antecedents OR respondent chains
  antecedents: AnyAntecedent[]
  consequences: FunctionalAnalysisItem[]
  modulatingVariables: FunctionalAnalysisItem[]
  functionalHypothesis: string
  originHypothesis?: string
  // Unconditioned behavior fields (remain for non-integrated analysis)
  habituation: FunctionalAnalysisItem[]
  sensitization: FunctionalAnalysisItem[]
}

export interface CaseData {
  caseName: string
  caseDescription?: string
  consultationDemands?: string[]
  patientId?: string
  patientName?: string
  age?: string
  sex?: string
  gender?: string
  referralReason?: string
  caseSummary?: string
  historicalAntecedents?: any[]
  responsePatterns?: any[]
  notes?: CaseNote[]
  selectedNoteIds?: string[]
  synthesisDraft?: SynthesisDraft
}

// New type for uploaded files
export interface UploadedFile {
  filename: string
  content: string
  caseId: string  // Associate file with specific case
}

// New type to encapsulate all data for one analysis case
export interface AnalysisCase {
  id: string; // Unique ID for this specific analysis instance
  name: string; // Display name for the case (e.g., Patient Name or ID)
  caseData: CaseData;
  analysisTargets: AnalysisTarget[]; // Paradigm-specific analysis targets
  files: UploadedFile[]; // Array of files associated with this case
  createdAt: number; // Timestamp for sorting or identification
}

type FBAContextType = {
  // Replaced single case state with multi-case state
  cases: AnalysisCase[];
  activeCaseId: string | null;
  setActiveCaseId: (id: string | null) => void;
  addAnalysisCase: (newCaseData: Partial<CaseData>, name?: string) => string; // Returns the new case ID
  updateAnalysisCase: (id: string, updates: Partial<Omit<AnalysisCase, 'id' | 'createdAt'>>) => void;
  deleteAnalysisCase: (id: string) => void;
  getActiveAnalysisCase: () => AnalysisCase | undefined;

  // Functions to modify the *active* case (convenience wrappers)
  updateActiveCaseData: (data: Partial<CaseData> | ((prevData: CaseData) => CaseData)) => void;
  addAnalysisTargetToActiveCase: (target: Omit<AnalysisTarget, 'id'>) => void; // Generate ID internally
  updateAnalysisTargetInActiveCase: (targetId: string, updatedTargetData: Partial<AnalysisTarget>) => void;
  removeAnalysisTargetFromActiveCase: (targetId: string) => void;

  // Step logic
  currentStep: number;
  setCurrentStep: (step: number) => void;
  extractedBehaviors: string[];
  setExtractedBehaviors: (behaviors: string[]) => void;

  // Add function to update case name
  updateAnalysisCaseName: (caseId: string, newName: string) => void;

  getActiveSynthesisDraft: () => SynthesisDraft;
  saveActiveSynthesisDraft: (draft: SynthesisDraft) => void;
  
  // NEW: Note management functions with centralized autosave
  addNoteToActiveCase: (noteData: Omit<CaseNote, 'id'>) => string | null;
  updateNoteInActiveCase: (noteId: string, updates: Partial<CaseNote>) => void;
  removeNoteFromActiveCase: (noteId: string) => void;
  // CHANGED: Multiple note selection functions
  toggleNoteSelection: (noteId: string) => void;
  getSelectedNotesForActiveCase: () => CaseNote[];
  getSelectedNotesContent: () => string; // NEW: Centralized content retrieval
  getSelectedNotesWordCount: () => number;
  isSelectedNotesOverLimit: () => boolean;
  getNotesForActiveCase: () => CaseNote[];
  activeNoteId: string | null;
  setActiveNote: (noteId: string | null) => void;
  getActiveNote: () => CaseNote | undefined;
  
  // NEW: Centralized autosave functions
  debouncedUpdateActiveCaseData: (data: Partial<CaseData>) => void;
  debouncedUpdateNoteInActiveCase: (noteId: string, updates: Partial<CaseNote>) => void;
}

export interface SynthesisSectionDraft {
  text: string;
  completed: boolean;
}

export interface SynthesisDraft {
  selectedBehaviorIds: string[];
  sections: {
    functionalAnalysisSummary: SynthesisSectionDraft;
    dispositionalVariables: SynthesisSectionDraft;
    originHypothesis: SynthesisSectionDraft;
    maintenanceHypothesis: SynthesisSectionDraft;
    treatmentProposals: SynthesisSectionDraft;
  };
  lastSavedAt?: number;
}

const normalizeSectionDraft = (section?: Partial<SynthesisSectionDraft>): SynthesisSectionDraft => ({
  text: safeString(section?.text),
  completed: Boolean(section?.completed),
});

const normalizeBehaviorIds = (ids?: unknown): string[] => {
  if (!Array.isArray(ids)) {
    return [];
  }
  const seen = new Set<string>();
  const normalized: string[] = [];
  ids.forEach(id => {
    if (typeof id === "string") {
      const trimmed = id.trim();
      if (trimmed && !seen.has(trimmed)) {
        seen.add(trimmed);
        normalized.push(trimmed);
      }
    }
  });
  return normalized;
};

export const createEmptySynthesisDraft = (): SynthesisDraft => ({
  selectedBehaviorIds: [],
  sections: {
    functionalAnalysisSummary: normalizeSectionDraft(),
    dispositionalVariables: normalizeSectionDraft(),
    originHypothesis: normalizeSectionDraft(),
    maintenanceHypothesis: normalizeSectionDraft(),
    treatmentProposals: normalizeSectionDraft(),
  },
});

export const normalizeSynthesisDraft = (draft?: Partial<SynthesisDraft> | null): SynthesisDraft => ({
  selectedBehaviorIds: normalizeBehaviorIds(draft?.selectedBehaviorIds),
  sections: {
    functionalAnalysisSummary: normalizeSectionDraft(draft?.sections?.functionalAnalysisSummary),
    dispositionalVariables: normalizeSectionDraft(draft?.sections?.dispositionalVariables),
    originHypothesis: normalizeSectionDraft(draft?.sections?.originHypothesis),
    maintenanceHypothesis: normalizeSectionDraft(draft?.sections?.maintenanceHypothesis),
    treatmentProposals: normalizeSectionDraft(draft?.sections?.treatmentProposals),
  },
  lastSavedAt: typeof draft?.lastSavedAt === "number" ? draft.lastSavedAt : undefined,
});

// Default values - not strictly needed for the main case state anymore
const defaultCaseData: CaseData = {
  caseName: "",
  caseDescription: "",
  consultationDemands: [],
  synthesisDraft: createEmptySynthesisDraft(),
}

// Create context
const FBAContext = createContext<FBAContextType | undefined>(undefined)

// Use a new local storage key to avoid conflicts with the old format
const LOCAL_STORAGE_KEY = "fbaMultiCaseAppState_v3"

// --- Factory functions for creating default analysis targets ---

/** Creates a default operant analysis target */
export const createDefaultOperantTarget = (
  initials?: Partial<Omit<OperantAnalysisTarget, 'learningType'>>
): OperantAnalysisTarget => ({
  id: initials?.id || Date.now().toString(),
  name: safeString(initials?.name) || "New Operant Behavior",
  description: safeString(initials?.description),
  learningType: 'operant',
  frequency: initials?.frequency || { value: null, unit: "day" },
  duration: initials?.duration || { value: null, unit: "minutes" },
  intensity: initials?.intensity || 0,
  antecedents: safeArray(initials?.antecedents),
  consequences: safeArray(initials?.consequences),
  modulatingVariables: safeArray(initials?.modulatingVariables),
  maintenanceHypothesis: safeString(initials?.maintenanceHypothesis) || "Maintenance hypothesis not specified",
  originHypothesis: safeString(initials?.originHypothesis),
})

/** Creates a default mixed respondent-operant analysis target */
export const createDefaultMixedRespondentTarget = (
  initials?: Partial<Omit<MixedRespondentOperantTarget, 'learningType'>>
): MixedRespondentOperantTarget => ({
  id: initials?.id || Date.now().toString(),
  name: safeString(initials?.name) || "New Mixed Respondent Behavior",
  description: safeString(initials?.description),
  learningType: 'respondent',
  frequency: initials?.frequency || { value: null, unit: "day" },
  duration: initials?.duration || { value: null, unit: "minutes" },
  intensity: initials?.intensity || 0,
  respondentChain: initials?.respondentChain || {
    item_type: 'respondent_chain',
    description: '',
    evidence_quote: null,
    us: [],
    ur: [],
    cs: [],
    cr: [],
  },
  antecedents: safeArray(initials?.antecedents),
  consequences: safeArray(initials?.consequences),
  modulatingVariables: safeArray(initials?.modulatingVariables),
  conditioningHypothesis: safeString(initials?.conditioningHypothesis) || "Conditioning hypothesis not specified",
  acquisitionHypothesis: safeString(initials?.acquisitionHypothesis),
})

/** Creates a default respondent analysis target */
export const createDefaultRespondentTarget = (
  initials?: Partial<Omit<RespondentAnalysisTarget, 'learningType'>>
): RespondentAnalysisTarget => ({
  id: initials?.id || Date.now().toString(),
  name: safeString(initials?.name) || "New Respondent Process",
  description: safeString(initials?.description),
  learningType: 'respondent_chain',
  conditioningChains: safeArray(initials?.conditioningChains),
  conditioningHypothesis: safeString(initials?.conditioningHypothesis) || "Conditioning hypothesis not specified",
  acquisitionHypothesis: safeString(initials?.acquisitionHypothesis),
})

/** Creates a default non-associative analysis target */
export const createDefaultNonAssociativeTarget = (
  initials?: Partial<Omit<NonAssociativeAnalysisTarget, 'learningType'>>
): NonAssociativeAnalysisTarget => ({
  id: initials?.id || Date.now().toString(),
  name: safeString(initials?.name) || "New Non-Associative Process",
  description: safeString(initials?.description),
  learningType: 'non_associative',
  habituationFactors: safeArray(initials?.habituationFactors),
  sensitizationFactors: safeArray(initials?.sensitizationFactors),
  adaptationHypothesis: safeString(initials?.adaptationHypothesis) || "Adaptation hypothesis not specified",
  exposureHistoryHypothesis: safeString(initials?.exposureHistoryHypothesis),
})

// --- Legacy compatibility function (for migration) ---
export const createDefaultBehavior = (
  initials?: Partial<LegacyBehavior>
): LegacyBehavior => ({
  id: initials?.id || Date.now().toString(),
  name: safeString(initials?.name) || "New Behavior",
  description: safeString(initials?.description),
  type: initials?.type || "operant",
  frequency: initials?.frequency || { value: 0, unit: "day" },
  duration: initials?.duration || { value: 0, unit: "minutes" },
  intensity: initials?.intensity || 0,
  antecedents: safeArray(initials?.antecedents),
  consequences: safeArray(initials?.consequences),
  modulatingVariables: safeArray(initials?.modulatingVariables),
  functionalHypothesis: safeString(initials?.functionalHypothesis),
  originHypothesis: safeString(initials?.originHypothesis),
  habituation: safeArray(initials?.habituation),
  sensitization: safeArray(initials?.sensitization),
})

// Provider component
export const FBAProvider = ({ children }: { children: React.ReactNode }) => {
  // Server-side safe initialization
  const [isClient, setIsClient] = useState(false)
  const [isHydrated, setIsHydrated] = useState(false)

  // --- New Multi-Case State ---
  const [cases, setCases] = useState<AnalysisCase[]>([])
  const [activeCaseId, setActiveCaseIdInternal] = useState<string | null>(null)

  // --- Existing State (Potentially needs refactoring later) ---
  const [currentStep, setCurrentStep] = useState(0) // Keep global for now
  const [extractedBehaviors, setExtractedBehaviors] = useState<string[]>([]) // Keep global for now
  
  // --- NEW: Active Note State ---
  const [activeNoteId, setActiveNoteId] = useState<string | null>(null)

  // --- NEW: Centralized Autosave with Debouncing ---
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const noteDebounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Centralized debounced case data update
  const debouncedUpdateActiveCaseData = useCallback((data: Partial<CaseData>) => {
    if (!activeCaseId) return;
    
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
    
    debounceTimeoutRef.current = setTimeout(() => {
      setCases(prevCases =>
        prevCases.map(c => {
          if (c.id === activeCaseId) {
            const newCaseData = { ...c.caseData, ...data };
            console.log("[Context] Auto-saved case data for:", activeCaseId);
            return { ...c, caseData: newCaseData };
          }
          return c;
        })
      );
    }, 750);
  }, [activeCaseId]);

  // Centralized debounced note update
  const debouncedUpdateNoteInActiveCase = useCallback((noteId: string, updates: Partial<CaseNote>) => {
    if (!activeCaseId) return;
    
    if (noteDebounceTimeoutRef.current) {
      clearTimeout(noteDebounceTimeoutRef.current);
    }
    
    noteDebounceTimeoutRef.current = setTimeout(() => {
      setCases(prevCases =>
        prevCases.map(c => {
          if (c.id === activeCaseId && c.caseData.notes) {
            const updatedNotes = c.caseData.notes.map(note => {
              if (note.id === noteId) {
                const updatedNote = { 
                  ...note, 
                  ...updates,
                  content: safeString(updates.content !== undefined ? updates.content : note.content),
                  title: safeString(updates.title !== undefined ? updates.title : note.title),
                  wordCount: safeWordCount(updates.content !== undefined ? updates.content : note.content),
                  lastModified: Date.now()
                };
                console.log("[Context] Auto-saved note:", noteId);
                return updatedNote;
              }
              return note;
            });
            return {
              ...c,
              caseData: {
                ...c.caseData,
                notes: updatedNotes
              }
            };
          }
          return c;
        })
      );
    }, 500); // Faster debounce for notes
  }, [activeCaseId]);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      if (noteDebounceTimeoutRef.current) {
        clearTimeout(noteDebounceTimeoutRef.current);
      }
    };
  }, []);

  // Detect client-side rendering
  useEffect(() => {
    setIsClient(true)
  }, [])

  // --- Active Case ID Setter ---
  const setActiveCaseId = (id: string | null) => {
    // Ensure the ID exists in the cases array if not null
    if (id !== null && !cases.some(c => c.id === id)) {
      console.warn(`Attempted to set active case ID to non-existent case: ${id}`);
      setActiveCaseIdInternal(null); // Or set to the first available case?
    } else {
      setActiveCaseIdInternal(id);
      // Reset active note when changing cases
      setActiveNoteId(null);
    }
  };

  // --- Core Case Management Functions ---
  const addAnalysisCase = (newCasePartialData: Partial<CaseData>, name?: string): string => {
    const newId = uuidv4();
    const caseName = safeString(name || newCasePartialData.caseName) || `Case ${safeArray(cases).length + 1}`;
    const now = Date.now();

    const fullCaseData: CaseData = {
      ...defaultCaseData,
      ...newCasePartialData,
      notes: safeArray(newCasePartialData.notes),
      selectedNoteIds: safeArray(newCasePartialData.selectedNoteIds),
      consultationDemands: safeArray(newCasePartialData.consultationDemands),
    };

    fullCaseData.synthesisDraft = normalizeSynthesisDraft(newCasePartialData.synthesisDraft ?? defaultCaseData.synthesisDraft);

    const newCase: AnalysisCase = {
      id: newId,
      name: caseName,
      caseData: fullCaseData,
      analysisTargets: [],
      files: [],
      createdAt: now,
    };

    setCases(prevCases => safeArray([...safeArray(prevCases), newCase]));
    setActiveCaseIdInternal(newId);
    return newId;
  };

  const updateAnalysisCase = (id: string, updates: Partial<Omit<AnalysisCase, 'id' | 'createdAt'>>) => {
    setCases(prevCases =>
      prevCases.map(c => {
        if (c.id === id) {
          const updatedCase = { ...c };
          if (updates.name !== undefined) updatedCase.name = updates.name;
          if (updates.caseData !== undefined) {
            const mergedCaseData: CaseData = { ...c.caseData, ...updates.caseData };
            mergedCaseData.consultationDemands = safeArray(mergedCaseData.consultationDemands);
            mergedCaseData.notes = safeArray(mergedCaseData.notes);
            mergedCaseData.selectedNoteIds = safeArray(mergedCaseData.selectedNoteIds);
            mergedCaseData.synthesisDraft = normalizeSynthesisDraft(mergedCaseData.synthesisDraft ?? c.caseData.synthesisDraft);
            updatedCase.caseData = mergedCaseData;
          }
          if (updates.analysisTargets !== undefined) updatedCase.analysisTargets = updates.analysisTargets;
          if (updates.files !== undefined) updatedCase.files = updates.files;
          return updatedCase;
        }
        return c;
      })
    );
  };

  const deleteAnalysisCase = (caseId: string) => {
    setCases(prevCases => prevCases.filter(c => c.id !== caseId));
    // If the deleted case was active, deactivate it
    if (activeCaseId === caseId) {
      setActiveCaseId(null);
    }
  };

  // Add function to update case name
  const updateAnalysisCaseName = (caseId: string, newName: string) => {
    setCases(prevCases =>
      prevCases.map(c => (c.id === caseId ? { ...c, name: newName.trim() } : c))
    );
  };

  // Function to get the currently active case object
  const getActiveAnalysisCase = (): AnalysisCase | undefined => {
    return cases.find(c => c.id === activeCaseId);
  };
  const getActiveSynthesisDraft = (): SynthesisDraft => {
    const activeCase = getActiveAnalysisCase();
    return normalizeSynthesisDraft(activeCase?.caseData?.synthesisDraft);
  };

  // --- Convenience Functions for Active Case ---
  const updateActiveCaseData = (data: Partial<CaseData> | ((prevData: CaseData) => CaseData)) => {
    if (!activeCaseId) {
      console.warn("Cannot update active case data: No active case selected.");
      return;
    }
    setCases(prevCases =>
      prevCases.map(c => {
        if (c.id === activeCaseId) {
          const partialCaseData = typeof data === 'function' ? data(c.caseData) : { ...c.caseData, ...data };
          const mergedCaseData: CaseData = { ...c.caseData, ...partialCaseData };
          mergedCaseData.consultationDemands = safeArray(mergedCaseData.consultationDemands);
          mergedCaseData.notes = safeArray(mergedCaseData.notes);
          mergedCaseData.selectedNoteIds = safeArray(mergedCaseData.selectedNoteIds);
          mergedCaseData.synthesisDraft = normalizeSynthesisDraft(mergedCaseData.synthesisDraft ?? c.caseData.synthesisDraft);
          return { ...c, caseData: mergedCaseData };
        }
        return c;
      })
    );
  };

  const saveActiveSynthesisDraft = (draft: SynthesisDraft) => {
    if (!activeCaseId) {
      console.warn('Cannot save synthesis draft: No active case selected.');
      return;
    }
    updateActiveCaseData(prevData => ({
      ...prevData,
      synthesisDraft: normalizeSynthesisDraft(draft),
    }));
  };

  const addAnalysisTargetToActiveCase = (targetData: Omit<AnalysisTarget, 'id'>) => {
    if (!activeCaseId) return;

    // For now, create the appropriate target type based on learningType
    // This will be enhanced when we add UI for selecting learning types
    let newTarget: AnalysisTarget;
    const id = uuidv4();

    switch (targetData.learningType) {
      case 'operant':
        newTarget = { ...createDefaultOperantTarget(targetData), id };
        break;
      case 'respondent':
        newTarget = { ...createDefaultMixedRespondentTarget(targetData), id };
        break;
      case 'respondent_chain':
        newTarget = { ...createDefaultRespondentTarget(targetData), id };
        break;
      case 'non_associative':
        newTarget = { ...createDefaultNonAssociativeTarget(targetData), id };
        break;
      default:
        // Default to operant
        newTarget = { ...createDefaultOperantTarget(targetData as any), id };
    }

    setCases(prevCases =>
      prevCases.map(c =>
        c.id === activeCaseId
          ? { ...c, analysisTargets: [...c.analysisTargets, newTarget] }
          : c
      )
    );
  };

  const updateAnalysisTargetInActiveCase = (targetId: string, updatedTargetData: Partial<AnalysisTarget>) => {
    if (!activeCaseId) return;

    setCases(prevCases =>
      prevCases.map(c => {
        if (c.id === activeCaseId) {
          return {
            ...c,
            analysisTargets: c.analysisTargets.map(target => {
              if (target.id === targetId) {
                // If learningType changes, replace the target to avoid mixing schemas
                if (
                  updatedTargetData.learningType &&
                  updatedTargetData.learningType !== target.learningType
                ) {
                  return { ...updatedTargetData } as AnalysisTarget;
                }
                // Otherwise, merge partial updates
                return { ...target, ...updatedTargetData } as AnalysisTarget;
              }
              return target;
            }),
          };
        }
        return c;
      })
    );
  };

  const removeAnalysisTargetFromActiveCase = (targetId: string) => {
    if (!activeCaseId) return;
    setCases(prevCases =>
      prevCases.map(c =>
        c.id === activeCaseId
          ? { ...c, analysisTargets: c.analysisTargets.filter(t => t.id !== targetId) }
          : c
      )
    );
  };

  // --- NEW: Note Management Functions ---
  
  // Add a note to the active case
  const addNoteToActiveCase = (noteData: Omit<CaseNote, 'id'>) => {
    if (!activeCaseId) return null;
    
    const newNoteId = uuidv4();
    const newNote: CaseNote = {
      ...noteData,
      id: newNoteId,
      title: safeString(noteData.title) || "Untitled Note",
      content: safeString(noteData.content),
      wordCount: safeWordCount(noteData.content),
      createdAt: noteData.createdAt || Date.now(),
      lastModified: noteData.lastModified || Date.now()
    };

    setCases(prevCases =>
      safeArray(prevCases).map(c => {
        if (c.id === activeCaseId) {
          const currentNotes = safeArray(c.caseData.notes);
          const updatedNotes = [...currentNotes, newNote];
          // If this is the first note, select it for analysis
          const currentSelectedIds = safeArray(c.caseData.selectedNoteIds);
          const updatedSelectedNoteIds = currentSelectedIds.length === 0 && updatedNotes.length === 1 
            ? [newNoteId] 
            : currentSelectedIds;
          
          return {
            ...c,
            caseData: {
              ...c.caseData,
              notes: updatedNotes,
              selectedNoteIds: updatedSelectedNoteIds
            }
          };
        }
        return c;
      })
    );
    
    // Set as active note
    setActiveNoteId(newNoteId);
    return newNoteId;
  };

  // Update a note in the active case
  const updateNoteInActiveCase = (noteId: string, updates: Partial<CaseNote>) => {
    if (!activeCaseId || !noteId) return;
    
    setCases(prevCases =>
      safeArray(prevCases).map(c => {
        if (c.id === activeCaseId) {
          const currentNotes = safeArray(c.caseData.notes);
          if (currentNotes.length === 0) return c;
          
          return {
            ...c,
            caseData: {
              ...c.caseData,
              notes: currentNotes.map(note => {
                if (note.id === noteId) {
                  return { 
                    ...note, 
                    ...updates,
                    title: safeString(updates.title !== undefined ? updates.title : note.title),
                    content: safeString(updates.content !== undefined ? updates.content : note.content),
                    wordCount: safeWordCount(updates.content !== undefined ? updates.content : note.content),
                    lastModified: Date.now() // Always update modification time
                  };
                }
                return note;
              })
            }
          };
        }
        return c;
      })
    );
  };

  // Remove a note from the active case
  const removeNoteFromActiveCase = (noteId: string) => {
    if (!activeCaseId || !noteId) return;
    
    setCases(prevCases =>
      safeArray(prevCases).map(c => {
        if (c.id === activeCaseId) {
          const currentNotes = safeArray(c.caseData.notes);
          const currentSelectedIds = safeArray(c.caseData.selectedNoteIds);
          
          // Remove from selected notes if it was selected
          const updatedSelectedNoteIds = currentSelectedIds.filter(id => id !== noteId);
          
          // If removing the active note, reset active note
          if (activeNoteId === noteId) {
            const remainingNotes = currentNotes.filter(n => n.id !== noteId);
            setActiveNoteId(remainingNotes.length > 0 ? remainingNotes[0].id : null);
          }
          
          return {
            ...c,
            caseData: {
              ...c.caseData,
              notes: currentNotes.filter(n => n.id !== noteId),
              selectedNoteIds: updatedSelectedNoteIds
            }
          };
        }
        return c;
      })
    );
  };

  // Toggle note selection for analysis
  const toggleNoteSelection = (noteId: string) => {
    if (!activeCaseId || !noteId) return;
    
    setCases(prevCases =>
      safeArray(prevCases).map(c => {
        if (c.id === activeCaseId) {
          const currentSelected = safeArray(c.caseData.selectedNoteIds);
          let updatedSelected: string[];
          
          if (currentSelected.includes(noteId)) {
            // Remove from selection
            updatedSelected = currentSelected.filter(id => id !== noteId);
          } else {
            // Add to selection
            updatedSelected = [...currentSelected, noteId];
          }
          
          return {
            ...c,
            caseData: {
              ...c.caseData,
              selectedNoteIds: updatedSelected
            }
          };
        }
        return c;
      })
    );
  };

  // Get all notes for the active case
  const getNotesForActiveCase = (): CaseNote[] => {
    const activeCase = safeArray(cases).find(c => c.id === activeCaseId);
    return safeArray(activeCase?.caseData.notes);
  };

  // Get selected notes for analysis
  const getSelectedNotesForActiveCase = (): CaseNote[] => {
    const activeCase = safeArray(cases).find(c => c.id === activeCaseId);
    if (!activeCase) return [];
    
    const notes = safeArray(activeCase.caseData.notes);
    const selectedIds = safeArray(activeCase.caseData.selectedNoteIds);
    
    if (notes.length === 0 || selectedIds.length === 0) return [];
    
    return notes.filter(note => selectedIds.includes(note.id));
  };

  // NEW: Get the combined content of selected notes
  const getSelectedNotesContent = (): string => {
    const selectedNotes = getSelectedNotesForActiveCase();
    if (selectedNotes.length === 0) return "";

    return selectedNotes
      .map(note => safeString(note?.content))
      .filter(content => content.trim().length > 0)
      .join('\n\n');
  };

  const getSelectedNotesWordCount = (): number => {
    const selectedNotes = getSelectedNotesForActiveCase();
    return selectedNotes.reduce((total, note) => {
      if (!note) {
        return total;
      }

      const storedWordCount = typeof note.wordCount === "number" ? note.wordCount : undefined;
      const words = storedWordCount !== undefined ? storedWordCount : safeWordCount(note.content);
      return total + words;
    }, 0);
  };

  const isSelectedNotesOverLimit = (): boolean => {
    return getSelectedNotesWordCount() > MAX_ANALYSIS_WORDS;
  };

  // Get the active note (currently being edited)
  const getActiveNote = (): CaseNote | undefined => {
    if (!activeNoteId) return undefined;
    
    const activeCase = safeArray(cases).find(c => c.id === activeCaseId);
    if (!activeCase) return undefined;
    
    const notes = safeArray(activeCase.caseData.notes);
    return notes.find(n => n.id === activeNoteId);
  };

  // Set which note is currently being edited
  const setActiveNote = (noteId: string | null) => {
    setActiveNoteId(noteId);
  };

  // --- Migrate legacy data ---
  const migrateLegacyCaseDescription = useCallback(() => {
    setCases(prevCases => 
      safeArray(prevCases).map(c => {
        // Only process cases with caseDescription but no notes
        const caseDescription = safeString(c.caseData?.caseDescription);
        const existingNotes = safeArray(c.caseData?.notes);
        
        if (caseDescription && existingNotes.length === 0) {
          const newNoteId = uuidv4();
          const migratedNote: CaseNote = {
            id: newNoteId,
            title: "Case Description",
            content: caseDescription,
            source: 'manual',
            createdAt: c.createdAt || Date.now(),
            lastModified: Date.now(),
            wordCount: safeWordCount(caseDescription)
          };
          
          return {
            ...c,
            caseData: {
              ...c.caseData,
              notes: [migratedNote],
              selectedNoteIds: [newNoteId]
            }
          };
        }
        return c;
      })
    );
  }, []);

  // --- Migrate legacy antecedent structure and normalize behavior types ---
  // Move legacy MO antecedents into modulating variables as motivational states.
  const migrateLegacyAntecedents = useCallback(() => {
    setCases(prevCases =>
      safeArray(prevCases).map(c => {
        if (!c.analysisTargets || c.analysisTargets.length === 0) {
          return c;
        }

        let didUpdate = false;
        const updatedTargets = safeArray(c.analysisTargets).map(target => {
          if (target.learningType !== "operant" && target.learningType !== "respondent") {
            return target;
          }

          const antecedents = safeArray((target as any).antecedents);
          const modulatingVariables = safeArray((target as any).modulatingVariables);
          const moAntecedents = antecedents.filter(
            (item: any) => item?.item_type === "operant_antecedent" && item?.type === "Motivating Operation (MO)",
          );

          if (moAntecedents.length === 0) {
            return target;
          }

          const remainingAntecedents = antecedents.filter(
            (item: any) => !(item?.item_type === "operant_antecedent" && item?.type === "Motivating Operation (MO)"),
          );

          const movedModulators = moAntecedents.map((item: any) => ({
            type: "Motivating Operation (MO)",
            description: item?.description || "",
            evidence_quote: item?.evidence_quote ?? null,
          }));

          didUpdate = true;
          return {
            ...target,
            antecedents: remainingAntecedents,
            modulatingVariables: [...modulatingVariables, ...movedModulators],
          } as AnalysisTarget;
        });

        return didUpdate ? { ...c, analysisTargets: updatedTargets } : c;
      }),
    );
  }, []);

  // --- Migrate legacy behaviors to analysis targets ---
  const migrateLegacyBehaviors = useCallback(() => {
    setCases(prevCases =>
      safeArray(prevCases).map(c => {
        // Skip if already migrated (has analysisTargets)
        if (c.analysisTargets && c.analysisTargets.length > 0) {
          return c;
        }

        // Migrate legacy behaviors to analysis targets
        const legacyBehaviors = (c as any).behaviors || [];
        const migratedTargets: AnalysisTarget[] = safeArray(legacyBehaviors).map((legacy: any) =>
          migrateToAnalysisTarget(legacy as LegacyBehavior)
        );

        return {
          ...c,
          analysisTargets: migratedTargets,
        };
      })
    );
  }, []);

  // --- Effect for Hydration (Only runs on client) ---
  useEffect(() => {
    // Skip hydration during SSR
    if (!isClient) {
      return;
    }
    
    try {
      const storedState = localStorage.getItem(LOCAL_STORAGE_KEY)
      if (storedState) {
        const parsedState = JSON.parse(storedState)
        if (parsedState) {
          // Restore multi-case state with proper null checks
          const casesArray = Array.isArray(parsedState.cases) ? parsedState.cases as any[] : [];
          const normalizedCases = casesArray.map(caseEntry => ({
            ...caseEntry,
            // Handle both old behaviors and new analysisTargets fields
            behaviors: safeArray(caseEntry.behaviors), // Keep for migration
            analysisTargets: safeArray(caseEntry.analysisTargets || []), // New field
            files: safeArray(caseEntry.files),
            caseData: {
              ...defaultCaseData,
              ...caseEntry.caseData,
              notes: safeArray(caseEntry.caseData?.notes),
              selectedNoteIds: safeArray(caseEntry.caseData?.selectedNoteIds),
              consultationDemands: safeArray(caseEntry.caseData?.consultationDemands),
              synthesisDraft: normalizeSynthesisDraft(caseEntry.caseData?.synthesisDraft),
            },
          }));
          setCases(normalizedCases)
          
          // Restore active ID only if it exists in the restored cases
          const restoredActiveId = safeString(parsedState.activeCaseId) || null;
          if (restoredActiveId && casesArray.some((c: AnalysisCase) => c.id === restoredActiveId)) {
            setActiveCaseIdInternal(restoredActiveId);
          } else {
            // If stored active ID is invalid or null, maybe set to the first case?
            setActiveCaseIdInternal(casesArray.length > 0 ? casesArray[0].id : null);
          }

          // Restore other state (consider if these should be per-case)
          setCurrentStep(parsedState.currentStep || 0)
          setExtractedBehaviors(Array.isArray(parsedState.extractedBehaviors) ? parsedState.extractedBehaviors : [])
          
          // Migrate legacy data if needed
          setTimeout(() => {
            migrateLegacyCaseDescription();
            migrateLegacyAntecedents();
            migrateLegacyBehaviors();
          }, 100);
        }
      }
    } catch (error) {
      console.error("Failed to load state from localStorage:", error)
      // Only clear localStorage on client
      if (typeof window !== "undefined") {
        localStorage.removeItem(LOCAL_STORAGE_KEY); // Clear potentially corrupt state
      }
    } finally {
      setIsHydrated(true)
    }
  }, [isClient, migrateLegacyCaseDescription, migrateLegacyAntecedents, migrateLegacyBehaviors])

  // --- Effect for Persistence ---
  useEffect(() => {
    // Skip persistence during SSR
    if (!isClient) {
      return;
    }
    
    if (isHydrated) {
      try {
        const stateToSave = {
          // Persist multi-case state
          cases,
          activeCaseId,
          // Persist other state
          currentStep,
          extractedBehaviors,
          // REMOVED: Global file state
        }
        localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(stateToSave))
      } catch (error) {
        console.error("Failed to save state to localStorage:", error)
      }
    }
    // Add new state variables to dependency array
  }, [cases, activeCaseId, currentStep, extractedBehaviors, isHydrated, isClient])

  // REMOVED: File management functions (addFileToCase, removeFileFromCase, getFilesForCase)

  return (
    <FBAContext.Provider
      value={{
        // New multi-case state and functions
        cases,
        activeCaseId,
        setActiveCaseId,
        addAnalysisCase,
        updateAnalysisCase,
        deleteAnalysisCase,
        getActiveAnalysisCase,

        getActiveSynthesisDraft,

        // Convenience functions for active case

        updateActiveCaseData,

        saveActiveSynthesisDraft,
        addAnalysisTargetToActiveCase,
        updateAnalysisTargetInActiveCase,
        removeAnalysisTargetFromActiveCase,
        // Other state (keep for now)
        currentStep,
        setCurrentStep,
        extractedBehaviors,
        setExtractedBehaviors,
        
        // REMOVED: Global file handling state and functions
        // uploadedFiles,
        // addUploadedFile,
        // removeUploadedFile,
        // selectedUploadedFile,
        // setSelectedUploadedFile,
        
        // REMOVED: File management functions - now case-specific
        // addFileToCase,
        // removeFileFromCase,
        // getFilesForCase,
        
        // Add function to update case name
        updateAnalysisCaseName,
        
        // NEW: Note management functions
        addNoteToActiveCase,
        updateNoteInActiveCase,
        removeNoteFromActiveCase,
        getNotesForActiveCase,
        activeNoteId,
        setActiveNote,
        getActiveNote,
        // CHANGED: Multiple note selection functions
        toggleNoteSelection,
        getSelectedNotesForActiveCase,
        getSelectedNotesContent, // NEW
        getSelectedNotesWordCount,
        isSelectedNotesOverLimit,
        
        // NEW: Centralized autosave functions
        debouncedUpdateActiveCaseData,
        debouncedUpdateNoteInActiveCase,
      }}
    >
      {children}
    </FBAContext.Provider>
  )
}

// Custom hook to use the context
export function useFBA() {
  const context = useContext(FBAContext)
  if (context === undefined) {
    throw new Error("useFBA must be used within a FBAProvider")
  }
  return context
}





